import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { watchAuthState } from '../lib/firebase'
import { StoneImage } from '../components/StoneImage'
import { AppBadge } from '../components/AppLayout'
import { SiteHeader } from '../components/SiteHeader'
import { searchApi } from '../api/endpoints'
import { stoneKey } from '../lib/stoneKey'
import { displayStockId } from '../lib/format'
import type { Diamond } from '../api/types'

/** Cinematic hero background video (higgsfield-generated). Web-encoded
 *  H.264 + faststart so Chrome streams it; poster is the LCP frame. */
const HERO_VIDEO = '/diamonds/diaex-hero-v2.mp4'
const HERO_POSTER = '/diamonds/diaex-hero-v2-poster.jpg'

/** True when the visitor asked for reduced motion — the hero then shows
 *  the poster still instead of autoplaying the loop. SSR-safe. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])
  return reduced
}

const SUGGESTIONS = [
  'Round 1-2ct under $5k',
  'Oval D color VS+',
  'Biggest stone in stock',
  'Heart shape in stock',
]

/**
 * Landing — the DiaEx marketing homepage. Rendered on desktop AND to a
 * SIGNED-OUT visitor in a plain mobile browser tab at "/" (App.tsx's
 * isInstalledApp() carve-out): the installed app / app hostname / ?app=1 stay
 * on the auth-first phone shell, but first-time mobile-web visitors get this
 * page so they can see the pitch and sign up. It is therefore responsive down
 * to phone widths — the hero card is full-width + bottom-anchored on small
 * screens, and the two ivory sections' reach-in images are absolute inside
 * overflow-hidden so they never force a horizontal scroll. SiteHeader carries
 * its own mobile nav (Browse / Gemma / Sell / Sign in). Once signed in, the
 * mobile-web visitor gets the phone shell — this page is signed-out-mobile +
 * all-desktop.
 *
 * Chaptered scroll: Ch0 hero (a full-bleed cinematic loop of the
 * exchange, the locked copy sitting in a frosted-glass card),
 * Ch1 The Exchange (live inventory), Ch2 Certified (stats),
 * Ch3 suppliers, footer.
 *
 * LOCKED (Tej, 2026-08-03): brand renders exactly "DiaEx"; hero copy
 * is frozen verbatim; headline stays MODEST — the footage carries the
 * mood, never the type.
 *
 * Hero background = HERO_VIDEO (higgsfield-generated, web-encoded), a
 * scoped section background that fades into the light page below so it
 * flows into The Exchange. Honors prefers-reduced-motion (poster only).
 * Tokens are namespaced vault-* — nothing here leaks into the light app
 * or the mobile shell.
 */
export function Landing() {
  const [stones, setStones] = useState<Diamond[]>([])
  const [stonesLoading, setStonesLoading] = useState(true)
  const [total, setTotal] = useState<number | null>(null)
  const [signedIn, setSignedIn] = useState(false)

  // The fetched own-photo pool — backfill source when a marquee pick's photo
  // dies at LOAD time (dead supplier URL) and the card demotes itself. We
  // NEVER filter by photo host/URL up front (that was the photo-filter
  // incident) — media is server-attested; only a real onload failure demotes.
  const poolRef = useRef<Diamond[]>([])
  const demoteStone = (id: string) => {
    setStones((prev) => {
      const idx = prev.findIndex((s) => stoneKey(s) === id)
      if (idx < 0) return prev
      const used = new Set(prev.map(stoneKey))
      const next = poolRef.current.find((s) => !used.has(stoneKey(s)) && s.media?.imageUrl)
      const copy = [...prev]
      if (next) copy[idx] = next
      else copy.splice(idx, 1)
      return copy
    })
  }

  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState((user) => setSignedIn(!!user))
    } catch { /* signed-out rendering */ }
    return () => unsub?.()
  }, [])

  useEffect(() => {
    let cancelled = false
    // Feature real inventory carrying a photo, shape-DIVERSE, for the rolling
    // marquee (~12). Shuffle → one per distinct shape → backfill. Headline
    // count = the FULL catalog (this query is unfiltered).
    void (async () => {
      try {
        const feat = await searchApi.query({ limit: 120 })
        if (cancelled) return
        if (typeof feat.total === 'number') setTotal(feat.total)
        const pool = feat.results.filter((s) => s.media?.imageUrl)
        poolRef.current = pool
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[pool[i], pool[j]] = [pool[j], pool[i]]
        }
        const seen = new Set<string>()
        const picks: Diamond[] = []
        for (const s of pool) {
          const k = s.shape ?? '?'
          if (!seen.has(k)) { seen.add(k); picks.push(s); if (picks.length === 12) break }
        }
        for (const s of pool) {
          if (picks.length >= 12) break
          if (!picks.includes(s)) picks.push(s)
        }
        setStones(picks)
      } catch { /* page stands alone without live data */ }
      finally { if (!cancelled) setStonesLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="relative z-[1] min-h-dvh text-vault-cream">
      <div className="app-atmosphere" aria-hidden />

      <div className="relative z-10">
        <SiteHeader overlay />
        <VaultHero total={total} />
        <ExchangeSection stones={stones} signedIn={signedIn} loading={stonesLoading} total={total} onDemote={demoteStone} />
        <CertifiedSection total={total} />
        <SupplierSection />
        <VaultFooter />
      </div>
    </div>
  )
}

/* ─────────────────────────── chrome ─────────────────────────── */

/** Role claim of the signed-in user (null when signed out) — the landing's
 *  supplier CTAs are noise for someone who already IS a supplier. */
function useRole(): string | null {
  const [role, setRole] = useState<string | null>(null)
  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState(async (user) => {
        if (!user) { setRole(null); return }
        try {
          const tok = await user.getIdTokenResult()
          setRole(((tok.claims as { role?: string }).role) || 'buyer')
        } catch { setRole('buyer') }
      })
    } catch { setRole(null) }
    return () => unsub?.()
  }, [])
  return role
}

/* ─────────────────────── Ch0 — the vault ─────────────────────── */

/** Count-up for the live-stones stat — the "live market" beat of the
 *  entrance. Honors prefers-reduced-motion (jumps straight to target). */
function useCountUp(target: number, startDelayMs = 950, durationMs = 1300): number {
  const [v, setV] = useState(0)
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setV(target)
      return
    }
    let raf = 0
    const timer = window.setTimeout(() => {
      const t0 = performance.now()
      const tick = (t: number) => {
        const p = Math.min(1, (t - t0) / durationMs)
        const eased = 1 - Math.pow(1 - p, 3)
        setV(Math.round(target * eased))
        if (p < 1) raf = requestAnimationFrame(tick)
      }
      raf = requestAnimationFrame(tick)
    }, startDelayMs)
    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(raf)
    }
  }, [target, startDelayMs, durationMs])
  return v
}

/** One masked headline line — text rises out of a clipping wrapper, the
 *  "light through a slit" reveal. Pure transform; MotionConfig makes it
 *  instant under reduced motion. */
function MaskedLine({ children, delay }: { children: ReactNode; delay: number }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block"
        initial={{ y: '112%' }}
        animate={{ y: '0%' }}
        transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  )
}

function VaultHero({ total }: { total: number | null }) {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const reducedMotion = usePrefersReducedMotion()

  function go(text: string) {
    const q = text.trim()
    if (!q) {
      navigate('/chat')
      return
    }
    navigate(`/chat?q=${encodeURIComponent(q)}`)
  }

  const count = useCountUp(total ?? 0)

  // Autoplay only when motion is welcome; reduced-motion visitors keep the
  // poster still (the <video> also carries the poster as its LCP frame).
  const videoRef = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (reducedMotion) {
      v.pause()
      return
    }
    // Some browsers need an explicit play() after hydration; ignore the
    // autoplay-rejection promise (muted+inline is allowed everywhere).
    v.play().catch(() => { /* poster stands in */ })
  }, [reducedMotion])

  // Scroll cue fades the moment the user starts moving — it has done
  // its job. rAF-throttled, passive.
  const cueRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const el = cueRef.current
        if (el) el.style.opacity = String(Math.max(0, 1 - window.scrollY / (window.innerHeight * 0.22)))
        ticking = false
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <section className="relative isolate min-h-[100svh] flex items-end md:items-center overflow-hidden">
      {/* ── Full-bleed cinematic loop of the exchange. object-cover, behind
          all content. muted+inline+loop autoplays everywhere; reduced-motion
          visitors get the poster still (paused above). ── */}
      <div
        className="absolute inset-0 -z-10"
        aria-hidden
        style={{
          WebkitMaskImage: 'linear-gradient(to bottom, #000 60%, transparent 98%)',
          maskImage: 'linear-gradient(to bottom, #000 60%, transparent 98%)',
        }}
      >
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          autoPlay={!reducedMotion}
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_POSTER}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Legibility scrim — darkens the footage under the card and, at the
            bottom, fades to the light page so the hero flows into The
            Exchange below (vault-void = the page background, #faf8f4). */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(105deg, rgba(6,17,36,0.74) 0%, rgba(6,17,36,0.52) 42%, rgba(6,17,36,0.14) 72%, rgba(6,17,36,0) 100%)',
          }}
        />
      </div>

      <div className="mx-auto w-full max-w-6xl px-6 pt-28 pb-24 md:pt-24 md:pb-16">
        {/* Frosted-glass overlay card — DiaEx dark navy (#0a1f3c) tint +
            heavy blur; light copy for legibility over the moving footage.
            Bottom-anchored + full width on mobile, left-set on desktop. */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-xl overflow-hidden rounded-[14px] border border-vault-gold/20 bg-gradient-to-br from-[#0a1f3c]/62 to-[#06111f]/48 p-8 backdrop-blur-2xl shadow-[0_40px_110px_-34px_rgba(6,17,36,0.78)] sm:p-9 md:p-10 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-vault-gold/70 before:to-transparent before:content-['']"
        >
          {/* Headline — LOCKED copy, MODEST scale; lines rise from behind
              a mask, staggered. */}
          <h1
            aria-label="The diamond exchange, built on trust."
            className="font-vault font-normal text-[clamp(2.15rem,3.4vw,3.35rem)] leading-[1.14] tracking-display text-white"
          >
            <span aria-hidden>
              <MaskedLine delay={0.4}>The diamond exchange,</MaskedLine>
              <MaskedLine delay={0.55}>built on trust.</MaskedLine>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 text-[15px] leading-relaxed text-white/80"
          >
            A free, transparent marketplace — direct from suppliers.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.5 }}
            className="mt-4 text-[11.5px] font-semibold uppercase tracking-[0.22em] text-vault-gold-hover tabular"
          >
            {total === null ? 'Loading live inventory…' : `${count.toLocaleString('en-US')} stones · live now`}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              type="button"
              onClick={() => navigate('/search')}
              className="inline-flex h-10 items-center justify-center rounded-full bg-vault-gold px-5 text-[13.5px] font-semibold text-vault-void hover:bg-vault-gold-hover active:scale-[0.98] transition-all duration-default ease-premium"
            >
              Browse Diamonds
            </button>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="inline-flex h-10 items-center justify-center rounded-full border border-white/25 bg-white/10 px-5 text-[13.5px] font-medium text-white hover:bg-white/20 hover:text-white active:scale-[0.98] transition-all duration-default"
            >
              Get Started
            </button>
          </motion.div>

          {/* Ask Gemma command bar — the chat-first handoff, LOCKED placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9"
          >
            <div className="vault-sheen flex items-center gap-3 rounded-full border border-white/20 bg-white/10 py-2.5 pl-5 pr-2.5 backdrop-blur-md">
              <SparkleIcon className="h-4 w-4 shrink-0 text-vault-gold-hover" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    go(input)
                  }
                }}
                maxLength={300}
                placeholder="Ask Gemma — 2ct oval under $8,000…"
                className="flex-1 bg-transparent text-[14px] text-white placeholder:text-white/55 focus:outline-none"
                data-testid="landing-input"
              />
              <button
                type="button"
                onClick={() => go(input)}
                aria-label="Send"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/25 bg-white/10 text-vault-gold-hover hover:bg-vault-gold hover:text-vault-void hover:border-transparent transition-colors duration-default"
                data-testid="landing-send"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.7, duration: 0.6 }}
              className="mt-3.5 flex flex-wrap gap-x-5 gap-y-1.5 pl-1"
            >
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => go(s)}
                  className="text-[12px] text-white/60 hover:text-white transition-colors duration-default"
                >
                  {s}
                </button>
              ))}
            </motion.div>
            {/* Text-us CTA — the SAME Gemma, over WhatsApp. Prefilled wa.me
                deep link so no path dead-ends at "email only". */}
            <motion.a
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.7, duration: 0.5 }}
              href="https://wa.me/18884309951?text=Hi%2C%20I%27m%20looking%20for%20a%20diamond"
              target="_blank"
              rel="noopener"
              data-testid="landing-whatsapp-cta"
              className="mt-4 inline-flex items-center gap-2 text-[13px] text-white/70 hover:text-vault-gold-hover transition-colors duration-default"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.6-6.1c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.6.8-.8 1-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.4-3c-.3-.4 0-.5.2-.7l.5-.6c.1-.2.1-.4 0-.5l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.9.9-1.2 2.1-.4 3.6a12 12 0 0 0 4.6 4.6c1.7.8 2.4.9 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.2-.1-.3-.1Z"/></svg>
              or text us on WhatsApp — +1 (888) 430-9951
            </motion.a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue — tells the visitor the live inventory is below.
          Fades out on first scroll; click glides to The Exchange. */}
      <motion.button
        ref={cueRef}
        type="button"
        onClick={() => document.getElementById('exchange')?.scrollIntoView({ behavior: 'smooth' })}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2.5 group md:flex"
        aria-label="Scroll to explore the live inventory"
      >
        <span className="vault-cue-glow text-[10px] font-semibold uppercase tracking-[0.26em]">
          Explore the Exchange
        </span>
        <span className="vault-cue-line" aria-hidden />
      </motion.button>
    </section>
  )
}

function SparkleIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12 2l2.1 6.2L20 10l-5.9 1.8L12 18l-2.1-6.2L4 10l5.9-1.8z" />
      <path d="M19 15l1 2.8 2.8 1-2.8 1-1 2.8-1-2.8-2.8-1 2.8-1z" opacity="0.85" />
    </svg>
  )
}

/* ──────────────────── Ch1 — the exchange ──────────────────── */

const FILTER_PILLS = [
  { label: 'Round', to: '/search' },
  { label: 'Oval', to: '/search' },
  { label: 'Under $10k', to: '/search' },
  { label: '1–2 ct', to: '/search' },
]

const QUERIES = [
  'under 2 ct',
  'over 5 ct, under $3,000',
  'VVS · D–F colour',
  'fancy yellow · cushion',
  'oval · 1–2 ct · VS+',
  'emerald cut · over 10 ct',
  'round brilliant · under $5k',
  'heart shape · in stock',
]

/** Types out example queries and cycles them — signals "we have everything". */
function TypewriterQueries() {
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const [i, setI] = useState(0)
  const [txt, setTxt] = useState('')
  const [deleting, setDeleting] = useState(false)
  useEffect(() => {
    if (reduce) return
    const full = QUERIES[i]
    let t: number
    if (!deleting && txt.length < full.length) {
      t = window.setTimeout(() => setTxt(full.slice(0, txt.length + 1)), 55)
    } else if (!deleting && txt.length === full.length) {
      t = window.setTimeout(() => setDeleting(true), 1500)
    } else if (deleting && txt.length > 0) {
      t = window.setTimeout(() => setTxt(full.slice(0, txt.length - 1)), 26)
    } else {
      setDeleting(false)
      setI((v) => (v + 1) % QUERIES.length)
    }
    return () => window.clearTimeout(t)
  }, [txt, deleting, i, reduce])
  return (
    <span className="font-medium text-vault-gold">
      {reduce ? QUERIES[0] : txt}
      {!reduce && <span className="ml-0.5 inline-block w-[1px] animate-pulse">|</span>}
    </span>
  )
}

/** Placeholder row while live inventory loads — reserves layout, shimmers. */
function SkeletonRow() {
  return (
    <div
      className="overflow-hidden"
      style={{
        WebkitMaskImage: 'linear-gradient(to right, transparent, #000 5%, #000 95%, transparent)',
        maskImage: 'linear-gradient(to right, transparent, #000 5%, #000 95%, transparent)',
      }}
    >
      <div className="flex gap-4 pl-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="w-[220px] shrink-0 animate-pulse overflow-hidden rounded-[16px] border border-vault-cream/[0.08] bg-vault-raised/40"
          >
            <div className="aspect-square w-full bg-vault-cream/[0.05]" />
            <div className="space-y-2 p-3.5">
              <div className="h-3 w-2/3 rounded bg-vault-cream/[0.07]" />
              <div className="h-2.5 w-1/2 rounded bg-vault-cream/[0.05]" />
              <div className="h-2.5 w-1/3 rounded bg-vault-cream/[0.05]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** One live-inventory card — fixed size, no entrance/hover motion, so the
 *  marquee row stays perfectly uniform (never overlaps). */
function MarqueeCard({
  s,
  signedIn,
  onOpen,
  onDemote,
}: {
  s: Diamond
  signedIn: boolean
  onOpen: () => void
  onDemote?: () => void
}) {
  const specs = `${s.shape ?? 'Stone'} · ${Number(s.carat || 0).toFixed(2)}ct${s.color ? ` · ${s.color}` : ''}${s.clarity ? ` ${s.clarity}` : ''}`
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group/c block w-[220px] shrink-0 overflow-hidden rounded-[16px] border border-vault-cream/[0.08] bg-gradient-to-b from-vault-card to-vault-raised text-left transition-transform duration-300 hover:-translate-y-1"
      data-stock-id={s.stockId}
    >
      <span className="relative block aspect-square w-full overflow-hidden bg-vault-raised">
        <StoneImage
          stone={s}
          width={440}
          imgClassName="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-premium group-hover/c:scale-[1.06]"
          onSubstitute={onDemote}
        />
      </span>
      <span className="block p-3.5">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[12px] font-semibold text-vault-cream mono-id">#{displayStockId(s)}</span>
          {s.lab && (
            <span className="shrink-0 rounded-full border border-vault-cream/15 px-1.5 py-0.5 text-[9px] tracking-wide text-vault-text-2">{s.lab}</span>
          )}
        </span>
        <span className="mt-1 block truncate text-[11.5px] text-vault-text-2">{specs}</span>
        <span className="mt-1 block text-[11.5px] text-vault-gold/90">
          {signedIn && s.priceUSD != null
            ? `$${Math.round(Number(s.priceUSD)).toLocaleString('en-US')}`
            : 'Sign in for price'}
        </span>
      </span>
    </button>
  )
}

/** Full-bleed rolling marquee of live stones — motion + breadth in one row. */
function DiamondMarquee({
  stones,
  signedIn,
  onOpen,
  onDemote,
}: {
  stones: Diamond[]
  signedIn: boolean
  onOpen: (s: Diamond) => void
  onDemote: (id: string) => void
}) {
  const loop = [...stones, ...stones]
  const seconds = Math.max(34, stones.length * 5)
  return (
    <div
      className="group relative overflow-hidden"
      style={{
        WebkitMaskImage:
          'linear-gradient(to right, transparent, #000 5%, #000 95%, transparent)',
        maskImage:
          'linear-gradient(to right, transparent, #000 5%, #000 95%, transparent)',
      }}
    >
      <style>{`@keyframes dxMarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        @media(prefers-reduced-motion:reduce){.dx-marquee{animation:none!important}}`}</style>
      <div
        className="dx-marquee flex w-max gap-4 pl-4 group-hover:[animation-play-state:paused]"
        style={{ animation: `dxMarquee ${seconds}s linear infinite` }}
      >
        {loop.map((s, idx) => (
          <MarqueeCard
            key={`${stoneKey(s)}-${idx}`}
            s={s}
            signedIn={signedIn}
            onOpen={() => onOpen(s)}
            onDemote={() => onDemote(stoneKey(s))}
          />
        ))}
      </div>
    </div>
  )
}

function ExchangeSection({ stones, signedIn, loading, total, onDemote }: { stones: Diamond[]; signedIn: boolean; loading: boolean; total: number | null; onDemote: (id: string) => void }) {
  const navigate = useNavigate()
  if (stones.length === 0 && !loading) return null
  return (
    <section id="exchange" className="relative py-24">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto max-w-6xl px-6"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-vault-gold">The Exchange</p>
        <h2 className="mt-3 font-vault font-normal text-[clamp(1.6rem,2.3vw,2.2rem)] tracking-h2 text-vault-cream">
          Live inventory, transparent and direct.
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-vault-text-2">
          Search anything — <TypewriterQueries />. Over{' '}
          {total ? (Math.floor(total / 1000) * 1000).toLocaleString('en-US') : '19,000'} stones, one row away.
        </p>
      </motion.div>

      <div className="mx-auto max-w-6xl px-6 mt-8 flex flex-wrap items-center gap-2.5">
        {FILTER_PILLS.map((f) => (
          <button
            key={f.label}
            type="button"
            onClick={() => navigate(f.to)}
            className="vault-glass rounded-full h-9 px-4 text-[12.5px] text-vault-text-2 hover:text-vault-cream transition-colors duration-default"
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => navigate('/search')}
          className="ml-auto text-[13px] font-medium text-vault-gold hover:text-vault-gold-hover transition-colors duration-default"
        >
          Browse all →
        </button>
      </div>

      <div className="mt-8">
        {stones.length > 0 ? (
          <DiamondMarquee
            stones={stones}
            signedIn={signedIn}
            onOpen={(s) => navigate(`/search?stockId=${encodeURIComponent(s.stockId)}`)}
            onDemote={onDemote}
          />
        ) : (
          <SkeletonRow />
        )}
      </div>
    </section>
  )
}


/* ──────────────────── Ch2 — certified ──────────────────── */

/** The trust pointers every serious diamond site carries — customer
 *  familiarity is the point (Tej, 2026-08-03). Same three as the light
 *  landing had; restaged for the vault. */
const TRUST = [
  { title: 'Full Transparency', body: 'Real data. Real prices. No middlemen.' },
  { title: 'Direct Connections', body: 'Deal directly with verified suppliers.' },
  { title: 'Certified & Trusted', body: 'IGI & GIA certificates, verified.' },
]

function GoldDiamond({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12 2 22 9 12 22 2 9z" />
    </svg>
  )
}

function CertifiedSection({ total }: { total: number | null }) {
  const stats = [
    { value: total ? `${total.toLocaleString('en-US')}+` : '19,000+', label: 'Diamonds in stock' },
    { value: '1,240+', label: 'Verified suppliers' },
    { value: '100%', label: 'Transparent pricing' },
    { value: '160+', label: 'Countries served' },
  ]
  return (
    <section className="relative overflow-hidden bg-[#faf8f4]">
      {/* The hand reaches in from the right — ring toward the centre — then
          dissolves into the ivory. Feathered on its left and lower edges so it
          emerges from the page rather than sitting in a hard frame. Absolute +
          pointer-events-none inside this overflow-hidden section, so at phone
          width (signed-out mobile web now lands here too) it stays a scoped
          background and never forces a horizontal scroll; sizing scales up at
          md/lg. */}
      <div
        className="pointer-events-none absolute right-0 top-0 z-0 h-[400px] w-[66%] md:h-[470px] lg:h-[540px] lg:w-[60%]"
        style={{
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, #000 30%), linear-gradient(to bottom, #000 60%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          maskImage:
            'linear-gradient(to right, transparent 0%, #000 30%), linear-gradient(to bottom, #000 60%, transparent 100%)',
          maskComposite: 'intersect',
        }}
      >
        <img
          src="/diamonds/diaex-hand-ring.jpg"
          alt="A hand wearing a certified round-brilliant diamond solitaire"
          className="h-full w-full object-cover object-[52%_36%]"
          loading="lazy"
        />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-6 py-24 lg:py-28">
        {/* Heading — left of the hand, which reaches toward it. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-md lg:max-w-lg lg:min-h-[280px]"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-vault-gold">
            Certified &middot; Transparent &middot; Direct
          </p>
          <h2 className="mt-4 font-vault font-normal leading-[1.02] tracking-h2 text-vault-cream text-[clamp(2.2rem,4vw,3.4rem)]">
            Certified.<br />Transparent.<br />Direct.
          </h2>
          <p className="mt-5 max-w-sm text-[14.5px] leading-relaxed text-vault-text-2">
            Every stone is independently graded and verified before it reaches you — real
            data, real prices, and a direct line to the supplier.
          </p>
        </motion.div>

        {/* Trust pointers — lifted onto a premium card so the trio reads as a
            considered panel, not copy floating on the ivory. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 rounded-2xl border border-vault-gold-line/25 bg-white/85 p-8 shadow-[0_28px_64px_-32px_rgba(12,31,58,0.42)] backdrop-blur-sm lg:mt-24 lg:p-10"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-vault-cream/[0.08]">
            {TRUST.map((t) => (
              <div key={t.title} className="sm:px-8 sm:first:pl-0 sm:last:pr-0">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-vault-gold-line/40 bg-vault-gold/[0.06] text-vault-gold">
                  <GoldDiamond className="h-4 w-4" />
                </span>
                <p className="mt-4 text-[15px] font-semibold text-vault-cream">{t.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-vault-text-3">{t.body}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="mt-14 h-px w-full bg-vault-cream/[0.08]" />

        <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-4">
          {stats.map((st, i) => (
            <motion.div
              key={st.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ delay: i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-vault text-4xl md:text-5xl text-vault-gold tabular leading-none">{st.value}</p>
              <p className="mt-2.5 text-[11px] uppercase tracking-[0.18em] text-vault-text-3">{st.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ──────────────────── Ch3 — suppliers ──────────────────── */

function SupplierSection() {
  const navigate = useNavigate()
  const role = useRole()
  // Already a supplier — the whole become-a-supplier pitch is noise.
  if (role === 'supplier') return null
  return (
    <section className="relative overflow-hidden bg-[#faf8f4]">
      {/* Mirror of the Certified hand: a jeweler's loupe reaches in from the
          LEFT, inspecting a stone, then dissolves into the ivory. Feathered on
          its right + lower edges so the copy on the right stays on clean ivory. */}
      <div
        className="pointer-events-none absolute left-0 top-0 z-0 h-[400px] w-[68%] md:h-[470px] lg:h-[540px] lg:w-[58%]"
        style={{
          WebkitMaskImage:
            'linear-gradient(to left, transparent 0%, #000 30%), linear-gradient(to bottom, #000 60%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          maskImage:
            'linear-gradient(to left, transparent 0%, #000 30%), linear-gradient(to bottom, #000 60%, transparent 100%)',
          maskComposite: 'intersect',
        }}
      >
        <img
          src="/diamonds/diaex-loupe.jpg"
          alt="A jeweler inspecting a diamond through a gold loupe"
          className="h-full w-full object-cover object-[46%_40%]"
          loading="lazy"
        />
      </div>

      <div className="relative z-10 mx-auto flex max-w-6xl justify-end px-6 py-24 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-md text-right lg:max-w-lg"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-vault-gold">For suppliers</p>
          <h2 className="mt-4 font-vault font-normal leading-[1.06] tracking-h2 text-vault-cream text-[clamp(1.9rem,3.2vw,2.9rem)]">
            List your stock where serious buyers already are.
          </h2>
          <p className="mt-5 ml-auto max-w-sm text-[14.5px] leading-relaxed text-vault-text-2">
            Deal directly with verified buyers. Real data, real prices — your inventory in front of the trade in minutes.
          </p>
          {/* Route to the SELL path, never Browse — /sell decides what to
              show: a signed-out visitor gets the become-a-supplier wizard, an
              already-signed-in buyer gets the lightweight in-app connect flow
              (no re-doing the whole signup). Hidden entirely for suppliers by
              the role gate above, mirroring SiteHeader's `role !== 'supplier'`. */}
          <button
            type="button"
            onClick={() => navigate('/sell')}
            className="mt-8 inline-flex h-11 items-center justify-center rounded-full bg-vault-gold px-6 text-[13.5px] font-semibold text-vault-void hover:bg-vault-gold-hover active:scale-[0.98] transition-all duration-default"
          >
            List your stock
          </button>
        </motion.div>
      </div>
    </section>
  )
}

/* ─────────────────────── footer ─────────────────────── */

/** Footer mirrors the ORIGINAL AppLayout footer structure — brand block,
 *  the Apple/Google Play badges (same AppBadge elements, same testids,
 *  same coming-soon behavior), legal links, heritage copy. */
function VaultFooter() {
  return (
    <footer className="relative border-t border-vault-cream/[0.07] bg-vault-void/60">
      <div className="mx-auto max-w-6xl px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
        <div className="text-center md:text-left">
          <p className="font-vault text-[17px] text-vault-cream">
            DiaEx <span className="font-sans text-[11px] text-vault-text-3">by Aska</span>
          </p>
          <p className="mt-1 text-[12px] text-vault-text-3">Mumbai &middot; New York</p>
          <a
            href="mailto:info@thediaex.com"
            className="mt-1.5 inline-block text-[12px] text-vault-text-2 hover:text-vault-gold transition-colors duration-default"
          >
            info@thediaex.com
          </a>
          <p className="mt-2.5 space-x-4 text-[11px] text-vault-text-3">
            <a href="/terms" className="hover:text-vault-cream transition-colors duration-default">Terms</a>
            <a href="/privacy" className="hover:text-vault-cream transition-colors duration-default">Privacy</a>
            <a href="/refunds" className="hover:text-vault-cream transition-colors duration-default">Refunds</a>
          </p>
        </div>
        <div className="flex items-center justify-center gap-2.5">
          <AppBadge platform="ios" />
          <AppBadge platform="android" />
        </div>
        <div className="text-center md:text-right text-[11.5px] text-vault-text-3">
          <p>20 years sourcing lab-grown diamonds</p>
          <p className="mt-1">IGI &middot; GIA certified inventory</p>
        </div>
      </div>
    </footer>
  )
}
